import { useState } from 'react'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { Button, Tooltip, Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import useStyles from './_styles'
import { PlannedExpertiseCardProps } from './_props'
import {
    ArrowForwardIcon,
    AttachementFilledIcon,
    DeleteRoundedIcon,
    HistoryOverdueIcon,
    HistoryRoundedSvg,
} from '../../../assets/icons/icons'
import { getPatientBirthLabel, getPatientSexLabel, getCivilityLabel } from '../../Patient/History/_utils'
import PlannedExpertiseDeleteDialog from './PlannedExpertiseDeleteDialog'
import { getDateInfoDayOnly } from '../../../utils/dates'

const PlannedExpertiseCard = ({ expertise, isPreview }: PlannedExpertiseCardProps) => {
    const { patient, nbDocuments, requiredExpert, id, startedAt: date, workflow } = expertise
    const birthdate = patient?.birthdate ?? ''
    const workflowName = workflow?.name ?? ''
    const classes = useStyles()
    const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState(false)
    const { isOverdue } = getDateInfoDayOnly(date)
    const patientAgeText = `${patient.age} ${patient.age === 1 ? 'an' : 'ans'}`
    const attachementLabel =
        nbDocuments > 0
            ? `${nbDocuments} ${nbDocuments === 1 ? 'pièce jointe' : 'pièces jointes'}`
            : 'Aucune pièce jointe'

    return (
        <div
            className={clsx(
                classes.PlannedExpertiseCardWrapper,
                isPreview && classes.PlannedExpertiseCardWrapperPreview,
            )}
        >
            <Tooltip
                placement="top"
                title={
                    isOverdue && !isDeletionDialogOpen
                        ? 'Cette téléexpertise a été initiée il y a au moins deux jours, veuillez la finaliser ou la supprimer'
                        : ''
                }
            >
                <div className={clsx(classes.PlannedExpertiseCard, isOverdue && classes.PlannedExpertiseCardOverdue)}>
                    <div
                        className={clsx(
                            classes.PlannedExpertiseCardHeader,
                            isOverdue && classes.PlannedExpertiseCardHeaderOverdue,
                        )}
                    >
                        <div className={classes.PlannedExpertiseCardHeaderItem}>
                            {isOverdue ? <HistoryOverdueIcon /> : <HistoryRoundedSvg />}
                            <Typography>Débutée le {dayjs(date).format('DD/MM/YYYY')}</Typography>
                        </div>
                        <Button
                            className={classes.PlannedExpertiseCardHeaderDelete}
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setIsDeletionDialogOpen(true)
                            }}
                        >
                            <DeleteRoundedIcon />
                        </Button>

                        <PlannedExpertiseDeleteDialog
                            isOpen={isDeletionDialogOpen}
                            plannedExpertiseId={id}
                            onClose={() => {
                                setIsDeletionDialogOpen(false)
                            }}
                        />
                    </div>
                    <div className={classes.PlannedExpertiseCardContent}>
                        <div className={classes.PlannedExpertiseCardContentItem}>
                            <Typography className={classes.PlannedExpertiseCardContentItemLabel}>Patient</Typography>
                            <Typography className={classes.PlannedExpertiseCardContentItemValue}>
                                {getCivilityLabel(patient.sex)} {patient.fullName}
                            </Typography>
                            <Typography className={classes.PlannedExpertiseCardContentItemInfo}>
                                {getPatientBirthLabel(patient.sex)} le {dayjs(birthdate).format('DD/MM/YYYY')} (
                                {patientAgeText}) - Sexe {getPatientSexLabel(patient.sex)}
                            </Typography>
                        </div>
                        <div className={classes.PlannedExpertiseCardContentItem}>
                            <Typography className={classes.PlannedExpertiseCardContentItemLabel}>Requis</Typography>
                            <Typography className={classes.PlannedExpertiseCardContentItemValue}>
                                {requiredExpert.fullName}
                            </Typography>
                            <Typography className={classes.PlannedExpertiseCardContentItemSpeciality}>
                                {requiredExpert.profession &&
                                requiredExpert.speciality &&
                                requiredExpert.speciality.trim().length > 0
                                    ? `${requiredExpert.profession} - ${requiredExpert.speciality}`
                                    : requiredExpert.profession || requiredExpert.speciality || ''}
                            </Typography>
                        </div>
                        <div className={classes.PlannedExpertiseAvis}>
                            <Typography className={classes.PlannedExpertiseAvisText}>{workflowName}</Typography>
                        </div>
                    </div>
                    <div className={classes.PlannedExpertiseCardBottom}>
                        <div className={classes.PlannedExpertiseCardBottomInfo}>
                            <AttachementFilledIcon className={classes.PlannedExpertiseCardBottomInfoIcon} />
                            <Typography className={classes.PlannedExpertiseCardBottomInfoLabel}>
                                {attachementLabel}
                            </Typography>
                        </div>
                        <div className={classes.PlannedExpertiseCardBottomButtonContainer}>
                            <Button
                                to={`/teleexpertise/workflow/${id}`}
                                component={Link}
                                disableElevation
                                variant="contained"
                                className={clsx(classes.PlannedExpertiseCardBottomButton, isOverdue)}
                            >
                                <span className={classes.PlannedExpertiseCardBottomButtonLabel}>Finaliser</span>
                                <ArrowForwardIcon className={classes.PlannedExpertiseCardBottomButtonIcon} />
                            </Button>
                        </div>
                    </div>
                </div>
            </Tooltip>
        </div>
    )
}

export default PlannedExpertiseCard
